


































import {
    Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import Locale from '@/core/interfaces/Locale';
import { Getter } from 'vuex-class';
import Application from '@/core/interfaces/Application';
import FormFooter from '@/components/FormFooter.vue';
import JsonEditor from '@/components/JsonEditor.vue';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { flattenJson, unflattenJson } from '@/core/flatten-json-helper';
import ExcelJS from 'exceljs';
import { mergeDeep } from '@/core/merge-deep';

type JsonObject = { [key: string]: string | JsonObject };

const EMPTY_TRANSLATION_PLACEHOLDER = '<EMPTY ([locale]=[value])>';

function replaceStringsWithEmpty(obj: JsonObject, fallbackLocale: string): JsonObject {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (typeof value === 'string') {
            if (value.startsWith('<EMPTY')) {
                acc[key] = value;
            } else {
                acc[key] = EMPTY_TRANSLATION_PLACEHOLDER
                    .replace('[locale]', fallbackLocale)
                    .replace('[value]', value);
            }
        } else {
            acc[key] = replaceStringsWithEmpty(value, fallbackLocale);
        }

        return acc;
    }, {} as JsonObject);
}

function removeEmptyFromObject(obj: JsonObject): JsonObject {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (typeof value === 'string' && value.startsWith('<EMPTY')) {
            return acc;
        }

        acc[key] = typeof value === 'string' ? value : removeEmptyFromObject(value);
        return acc;
    }, {} as JsonObject);
}

const APPLICATION_URLS = {
    hmf: {
        url: 'https://hmf.next.lely.cloud',
        fallbackLocale: 'nl',
    },
};

@Component({
    components: { JsonEditor, FormFooter },
})
export default class ApplicationTranslations extends Vue {
    @Prop({ required: true })
    application!: Application;

    @Getter('locales')
    locales!: Locale[];

    selectedLocale = '';

    uploadFile: File | null = null;

    value = '';

    loading = false;

    get apiUrl(): string {
        if (!this.selectedLocale) {
            return '';
        }

        return `/admin/applications/${this.application.code}/locales/${this.selectedLocale}`;
    }

    @Watch('apiUrl')
    async updateTranslations(): Promise<void> {
        if (!this.apiUrl) {
            this.value = '';
            return;
        }

        this.loading = true;

        const translations = await this.fetchAllTranslations(this.selectedLocale);

        this.loading = false;
        this.value = JSON.stringify(translations, null, 2);
    }

    async fetchAllTranslations(locale: string): Promise<JsonObject> {
        const applicationData = (await this.fetchApplicationTranslations(locale)) ?? {};
        const apiData = (await this.fetchApiTranslations(locale)) ?? {};

        return mergeDeep({}, applicationData, apiData);
    }

    async fetchApiTranslations(locale: string): Promise<JsonObject | null> {
        const url = `/admin/applications/${this.application.code}/locales/${locale}`;
        try {
            return (await axios.get(url, { silentErrorStatuses: [404] })).data || {};
        } catch (err) {
            return null;
        }
    }

    async fetchApplicationTranslations(locale: string): Promise<JsonObject | null> {
        if (!Object.keys(APPLICATION_URLS).includes(this.application.code)) {
            return null;
        }

        const { fallbackLocale, url } = APPLICATION_URLS[this.application.code as keyof typeof APPLICATION_URLS];

        const fetch = async(locale: string) => {
            try {
                const instance = axios.create();
                delete instance.defaults.headers.common.Authorization;
                return (await instance.get(`${url}/translations/${locale}.json`)).data;
            } catch (err) {
                return null;
            }
        };

        const fallbackTranslations = replaceStringsWithEmpty(await fetch(fallbackLocale) ?? {}, fallbackLocale);
        const selectedTranslations = await fetch(locale) ?? {};

        return mergeDeep({}, fallbackTranslations, selectedTranslations);
    }

    async save(value: string): Promise<void> {
        const json = JSON.parse(value);
        const cleanedJson = removeEmptyFromObject(json);

        await axios.put(this.apiUrl, cleanedJson);

        this.$emit('submitted');
    }

    async download(): Promise<void> {
        const filename = `translations-${this.application.code}.xlsx`;

        const allTranslations = await Promise.all(
            this.locales.map(locale => this.fetchAllTranslations(locale.code)),
        );

        const flattenedByLocale = allTranslations.map(t => flattenJson(t));

        const allKeys = [...new Set(flattenedByLocale.flatMap(t => Object.keys(t)))].sort();

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Translations');
        sheet.insertRow(1, ['Key', ...this.locales.map(l => l.code)]);

        allKeys.forEach((key, idx) => {
            sheet.insertRow(idx + 2, [key, ...flattenedByLocale.map(t => t[key] ?? '')]);
        });

        const xls64 = await workbook.xlsx.writeBuffer();

        saveAs(
            new Blob([xls64], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            filename,
        );
    }

    @Watch('uploadFile')
    async upload(): Promise<void> {
        if (!this.uploadFile) {
            return;
        }

        const reader = new FileReader();
        reader.readAsArrayBuffer(this.uploadFile);

        reader.onload = async() => {
            const workbook = new ExcelJS.Workbook();
            const file = await workbook.xlsx.load(reader.result as any);
            const sheet = file.getWorksheet(1);

            const result: Record<string, Record<string, string>> = {};
            this.locales.forEach(locale => { result[locale.code] = {}; });

            sheet.eachRow((row, rowIdx) => {
                if (rowIdx === 1) {
                    return;
                }

                const key = String(row.getCell(1).value);
                this.locales.forEach((locale, localeIdx) => {
                    const val = row.getCell(localeIdx + 2).value;
                    if (val !== null && val !== '') {
                        result[locale.code][key] = String(val);
                    }
                });
            });

            await Promise.all(
                this.locales.map(locale => axios.put(
                    `/admin/applications/${this.application.code}/locales/${locale.code}`,
                    unflattenJson(result[locale.code]),
                )),
            );

            this.uploadFile = null;
            this.$emit('submitted');
        };
    }
}
